<script setup>
import { SEGMENT_PAGE_DATA } from '@/config/analytics';
import { APPDOWNLOADBANNERLINKS } from '@/config/app-download-banner-links.js';
import { PROPERTY_TYPE_DEFAULT } from '@/config/property-types';

const { $tracking } = useNuxtApp();
const {
  t,
  locale,
} = useI18n();
const { useUrl } = useUrls();
const { buildSeo } = generateSeo();

// ### COMPOSABLES - APIs
const { getHomePageTags } = useApiSeo();
const { getGlobalEvents } = useApiEvents();

// ### STORES
const sessionStore = useSessionStore();
const languageStore = useLanguageStore();
const testingStore = useTestingStore();

// ### VARIABLES INITIALIZATION
const locationKey = 'homepage';
const propertyTypeObj = PROPERTY_TYPE_DEFAULT;
const entryPageName = 'HOMEPAGE';
const gtmPageName = 'homepage';
const gtmClassification = 'home';
const gtmQuancastPage = 'Homepage';
const segmentTracking = SEGMENT_PAGE_DATA.homepage;

// ### EXPERIMENTS
const campaigns = {
  globalTravelDeals: {
    enabled: testingStore.hasEnabledFeature('web_globalTravelDeals'),
    vars: testingStore.getFeatureVariables('web_globalTravelDeals'),
  },
  europeTravelDeals: {
    enabled: testingStore.hasEnabledFeature('web_europeTravelDeals'),
    vars: testingStore.getFeatureVariables('web_europeTravelDeals'),
  },
  blackFriday: {
    enabled: testingStore.hasEnabledFeature('web_blackFriday'),
    vars: testingStore.getFeatureVariables('web_blackFriday'),
  },
};
const isEventsFeatureEnabled = testingStore.hasEnabledFeature('web_enableOGevents');

// ### STATE
const isBlackFridayBannerEnabled = useState('isBlackFridayBannerEnabled', () => isCampaignEnabled('blackFriday'));
const isGlobalTravelDealsBannerEnabled = useState('isGlobalTravelDealsBannerEnabled', () => isCampaignEnabled('globalTravelDeals'));
const isEuropeTravelDealsBannerEnabled = useState('isEuropeTravelDealsBannerEnabled', () => isCampaignEnabled('europeTravelDeals'));

// ### COMPUTED
const hasAnyEnabledCampaign = computed(() => {
  return (
    isBlackFridayBannerEnabled.value
    || isGlobalTravelDealsBannerEnabled.value
    || isEuropeTravelDealsBannerEnabled.value
  );
});

const isLanguageEn = computed(() => {
  return locale?.value === 'en';
});

const getAppDownloadDetails = computed(() => {
  return {
    page: 'homepage',
    linkPaths: APPDOWNLOADBANNERLINKS?.HOMEPAGE,
    linkParams: 'search',
  };
});

// ### METHODS
function isCampaignEnabled(campaignName) {
  const campaignObject = campaigns[campaignName];

  if (!campaignObject || !campaignObject?.enabled) {
    return false;
  }

  const campaignIsActiveForHomepage = campaignObject?.vars?.homepage?.enabled || false;
  const campaignStartDate = campaignObject?.vars?.enabled_dates?.startDate || null;
  const campaignEndDate = campaignObject?.vars?.enabled_dates?.endDate || null;

  if (!campaignStartDate || !campaignEndDate) {
    return false;
  }

  // @devNote: Campaign periods are UTC calendar days (YYYY-MM-DD from Optimizely): active from 00:00:00Z on the start day to 23:59:59Z on the end day.
  const campaignStartMoment = `${campaignStartDate}T00:00:00Z`;
  const campaignEndMoment = `${campaignEndDate}T23:59:59Z`;

  const now = Date.now();
  const campaignIsActiveForToday = now >= Date.parse(campaignStartMoment) && now <= Date.parse(campaignEndMoment);

  return campaignIsActiveForHomepage && campaignIsActiveForToday;
}

// ### GLOBAL FEATURED EVENTS
const globalFeaturedEvents = isEventsFeatureEnabled ? await getGlobalEvents() : [];

// ### SEO
const seoData = await getHomePageTags();
const hrefUrls = useUrl.generateHrefLangAlternates('');
const pageInfoData = {
  canonicalUrl: useUrl.getStaticBaseURL(),
  ogImage: 'https://a.hwstatic.com/image/upload/f_auto,q_auto,h_600/v1647449187/pwa/hostelworld.png',
};
const tradReplacements = {
  PROPERTYTYPE: t(propertyTypeObj?.transcode_s),
  PROPERTYTYPES: t(propertyTypeObj?.transcode),
  YEAR: new Date().getFullYear(),
};

const seoMetaData = buildSeo.getMetaData(seoData, pageInfoData, tradReplacements, false, locationKey);

useHead(() => ({
  htmlAttrs: { lang: locale?.value },
  link: [
    {
      rel: 'canonical',
      href: seoMetaData?.canonicalUrl,
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://a.hwstatic.com/image/upload/f_auto,q_auto,h_277/pwa/hero.chats.png',
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://sc.hwstatic.com/pwa/arrow.svg',
      type: 'image/svg+xml',
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://sc.hwstatic.com/pwa/homepage/usps/usp1.png',
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://sc.hwstatic.com/pwa/homepage/usps/usp2.png',
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://sc.hwstatic.com/pwa/homepage/usps/usp3.png',
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://a.hwstatic.com/raw/upload/f_auto,q_auto/wds/illustrations/sm/calendar-confirmation.svg',
      type: 'image/svg+xml',
    },
    {
      rel: 'preload',
      as: 'image',
      href: 'https://sc.hwstatic.com/pwa/bg.mobile.svg',
      type: 'image/svg+xml',
    },
  ].concat(hrefUrls),
}));

useSeoMeta({
  title: () => seoMetaData?.title,
  ogTitle: () => seoMetaData?.ogTitle,
  description: () => seoMetaData?.description,
  ogDescription: () => seoMetaData?.ogDescription,
  ogUrl: () => seoMetaData?.canonicalUrl,
  ogImage: () => seoMetaData?.ogImage,
  ogImageAlt: () => seoMetaData?.ogDescription,
  ogType: () => 'website',
  keywords: () => seoMetaData?.keywords,
  robots: () => 'index, follow, maxImagePreview:large',
  twitterCard: () => 'summary_large_image',
  twitterTitle: () => seoMetaData?.ogTitle,
  twitterDescription: () => seoMetaData?.ogDescription,
  twitterImage: () => seoMetaData?.ogImage,
  twitterImageAlt: () => seoMetaData?.ogDescription,
});

// ### TRACKING
sessionStore.setEntryPoint(entryPageName);

onMounted(() => {
  const trackingPropertyType = propertyTypeObj?.type?.toLowerCase();

  const trackingInfo = {
    gtm: {
      gtmPropertyType: trackingPropertyType,
      gtmCanonicalUrl: seoMetaData?.canonicalUrl,
      gtmPageName,
      gtmClassification,
      gtmQuancastPage,
    },
    segment: {
      event_name: segmentTracking.VIEWED,
      page_type: segmentTracking.TYPE,
      google_modal_cohort: '',
      campaign_banner_displayed: hasAnyEnabledCampaign.value ? 'variation' : 'control',
      social_stats_section_displayed: 'Most_Booked_City|Hostel_Travellers|Chat_Travellers|Event_Joiners',
    },
  };

  if (isEventsFeatureEnabled) {
    const globalEventsForTracking = globalFeaturedEvents?.map((event) => event.id)?.join('|') || '';
    trackingInfo.segment.occasion_genius_events = globalEventsForTracking;
  }

  $tracking?.trackOnPageLoad(trackingInfo.gtm, trackingInfo.segment);
});
</script>

<script>
/* eslint-disable */
import { ca } from '@/lib/channelAttribution.js';

const entryPageNameJs = 'HOMEPAGE';

if (import.meta.client) {
  ca?.setChannelAttribution(null, entryPageNameJs);
}
/* eslint-enable */
</script>

<template>
  <LazyCommonHwAppDownloadBanner v-bind="getAppDownloadDetails" />

  <header class="homepage-header">
    <HomepageHwHeader />
  </header>

  <section class="homepage-section no-spacing">
    <PromotionsHwGlobalTravelDealsBanner v-if="isGlobalTravelDealsBannerEnabled" />
    <PromotionsHwEuropeTravelDealsBanner v-if="isEuropeTravelDealsBannerEnabled" />
    <PromotionsHwBlackFridayBanner v-if="isBlackFridayBannerEnabled" />
  </section>

  <HomepageHwSocialUsps class="homepage-section social-usps-section" />

  <HomepageHwSocialHighlights class="homepage-section social-highlights-section" />

  <HomepageHwGlobalEvents
    v-if="isEventsFeatureEnabled"
    class="homepage-section global-events-section"
    :events="globalFeaturedEvents"
  />

  <HomepageHwTrustCuesReviews class="trust-cues-section" />

  <LazyHomepageHwSeeWhoIsGoingSection />

  <LazyHomepageHwSayHello class="homepage-section double-spacing" />

  <LazyHomepageHwFromSoloToSocial />

  <LazyHomepageHwBestHostels />

  <LazyHomepageHwAppDownload />

  <LazyHomepageHwClimateNeutral v-if="isLanguageEn" />

  <LazyHomepageHwPopularPlaces />

  <LazyHomepageHwTipsAndAdventures />

  <LazyHomepageHwNewsletter />

  <LazyHomepageHwAppDownloadQr />

  <CommonHwLanguagePicker v-show="languageStore.isLanguagePickerOpen" />
</template>

<style lang="scss" scoped>
.homepage-header {
  margin-inline: auto;
  max-width: wds-rem(1440px);
}

.homepage-section {
  max-width: wds-rem(1440px);
  margin-inline: auto;
  padding-inline: $wds-spacing-m;
  margin-bottom: $wds-spacing-xxxl;

  &.no-limits {
    padding-inline: 0;
    margin-inline: 0;
    max-width: 100%;
  }

  &.no-spacing {
    margin-bottom: 0;
  }

  &.double-spacing {
    margin-block: $wds-spacing-xxxl;
  }
}

.social-usps-section {
  margin-bottom: $wds-spacing-l;
}

.social-highlights-section {
  margin-bottom: wds-rem(40px);
}

.global-events-section {
  margin-block: wds-rem(80px);
}

.trust-cues-section {
  padding-inline: 0;
  margin-inline: 0;
  margin-bottom: $wds-spacing-xxxl;
  max-width: 100%;
}

@include tablet-large {
  .social-usps-section {
    margin-bottom: $wds-spacing-m;
  }

  .homepage-section {
    padding-inline: wds-rem(24px);
  }

  .social-highlights-section {
    margin-bottom: $wds-spacing-l;
  }
}

@include desktop {
  .homepage-section {
    padding-inline: wds-rem(40px);
  }

  .trust-cues-section {
    max-width: wds-rem(1440px);
    padding-inline: wds-rem(40px);
    margin-inline: auto;
  }
}
</style>
